import { useQuery } from 'react-query';

export interface Item {
  modelId: string;
  contentId: string;
  title: string;
  content: {
    html: string;
  };
}

interface Data {
  infoModalList: {
    items: Item[];
  };
}

interface Response {
  data: Data;
}

export type Refetch = () => void;

interface ContentProps {
  key: string;
  baseUrl: string;
  variation: string;
}

const URL_PATH_DELIMITER = '/';

/**
 * Provides backwards compatibility with existing content paths
 * to allow for an incremental migration of the legacy info layers
 */
function extractContentKey(key: string) {
  if (key.includes(URL_PATH_DELIMITER)) {
    return key.split(URL_PATH_DELIMITER).pop();
  }

  return key;
}

function addQueryParamToURL(url: string, variation: string) {
  const urlObj = new URL(url);
  if (Boolean(variation)) {
    urlObj.searchParams.append('variation', variation);
  }
  return urlObj.toString();
}

const useContent = ({ key, baseUrl, variation }: ContentProps) => {
  const url = addQueryParamToURL(
    `${baseUrl}/${extractContentKey(key)}/html`,
    variation,
  );

  const { data, refetch, isError, isLoading, isFetching } = useQuery(
    ['content', key],
    (): Promise<Response> => fetch(url).then((res) => res.json()),
    {
      retry: false,
      useErrorBoundary: false,
    },
  );

  return {
    content: data?.data.infoModalList?.items?.[0],
    refetch,
    isError,
    isLoading,
    isFetching,
  };
};

export default useContent;
